import React, { useEffect, useMemo, useState } from "react"
import { useField } from "formik"
import { debounce } from "lodash-es"
import { AutocompleteCloseReason } from "@mui/material/useAutocomplete"
import { ConfigQuery, fetchUsersForCompany, UsersForCompanyResponse } from "../../createOrderApi"
import { ApiQueryAutocomplete } from "../../../../../core/api/components/ApiQueryAutocomplete"
import { SelectedMainUserState } from "../../../createOrder/createOrderTypes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { MeetingServiceConfigMode } from "./formTypes"

function transformData(data: UsersForCompanyResponse) {
    return (data.users || []).map((u) => ({
        id: u.id,
        name: u.displayName,
        email: u.email,
    }))
}

type ResponsibleAutocompleteProps = {
    name: string
    label: string
    user: SelectedMainUserState
    mode?: MeetingServiceConfigMode
}

export function ResponsibleAutocomplete({ name, label, user, mode }: ResponsibleAutocompleteProps) {
    const [search, setSearch] = useState("")
    const [debouncedSearch, setDebouncedSearch] = useState("")
    const [open, setOpen] = useState(false)
    const [hasTyped, setHasTyped] = useState(false)
    const [inputValue, setInputValue] = useState("")

    const autocompleteMode: MeetingServiceConfigMode = mode || MeetingServiceConfigMode.LOAD_ON_OPEN
    const isSearchAsYouType = autocompleteMode === MeetingServiceConfigMode.SEARCH_AS_YOU_TYPE
    const isLoadOnOpen = autocompleteMode === MeetingServiceConfigMode.LOAD_ON_OPEN

    const isSearchValid = debouncedSearch.length === 0 || debouncedSearch.length >= 2

    const enabled = (isSearchAsYouType && debouncedSearch.length >= 2) || (isLoadOnOpen && open && isSearchValid)

    const debounceSearchHandler = useMemo(() => debounce((value: string) => setDebouncedSearch(value), 300), [])

    useEffect(() => {
        debounceSearchHandler(search)
        return () => {
            debounceSearchHandler.cancel()
        }
    }, [search, debounceSearchHandler])

    const {
        data: companyUsers = [],
        isError,
        isLoading,
    } = useApiQuery(fetchUsersForCompany, {
        queryName: ConfigQuery.USERS_FOR_COMPANY,
        dependencies: {
            userId: user.userId,
            companyId: user.companyId,
            search: debouncedSearch,
        },
        select: transformData,
        enabled: enabled,
    })

    const [field, meta, helpers] = useField(name)

    const options = useMemo(() => {
        return [...(user ? [{ name: user.displayName, id: user.userId }] : []), ...companyUsers]
    }, [user, companyUsers])

    const handleInputChange = (_event: React.ChangeEvent<{}>, value: string) => {
        setInputValue(value)

        if (isSearchAsYouType || open) {
            setSearch(value)
            setHasTyped(true)
        }
    }

    const handleChange = (_event: React.SyntheticEvent, value: any) => {
        if (typeof value === "string") {
            helpers.setValue({ name: value })
        } else if (value && value.id) {
            helpers.setValue(value)
        } else {
            helpers.setValue(null)
        }

        if (isLoadOnOpen) {
            setSearch("")
            setDebouncedSearch("")
        }
    }

    const handleClose = (_event: React.SyntheticEvent, reason: AutocompleteCloseReason) => {
        setOpen(false)

        if (reason !== "selectOption") {
            const trimmed = inputValue.trim()

            if (trimmed && (!field.value?.id || field.value.name !== trimmed)) {
                helpers.setValue({ name: trimmed })
            }
        }

        if (!hasTyped && isLoadOnOpen) {
            setSearch("")
            setDebouncedSearch("")
        }
        setHasTyped(false)
    }

    return (
        <ApiQueryAutocomplete
            isLoading={open && search.length >= 2 && isLoading}
            isError={false}
            options={options}
            getOptionLabel={(option) => {
                if (typeof option === "string") return option
                return option?.name || ""
            }}
            value={field.value || null}
            onChange={handleChange}
            onInputChange={handleInputChange}
            onOpen={() => setOpen(true)}
            onClose={handleClose}
            size="small"
            slotProps={{
                textField: {
                    helperText: meta.touched && meta.error,
                    error: !!meta.error && meta.touched,
                },
            }}
            label={label}
            isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false
                return option.id === value.id
            }}
            filterOptions={(x) => x}
            freeSolo
        />
    )
}
